/**
 * SEO Utilities for managing meta tags and structured data
 */

/**
 * Updates the page meta tags for SEO
 * @param {Object} metaData - Object containing title, description, and keywords
 */
export const updateMetaTags = (metaData) => {
  // Update title
  document.title = metaData.title;
  
  // Update description
  updateMetaTag("description", metaData.description);
  
  // Update keywords
  updateMetaTag("keywords", metaData.keywords);
  
  // Update Open Graph tags
  updateOpenGraphTag("og:title", metaData.title);
  updateOpenGraphTag("og:description", metaData.description);
  
  // Update Twitter Card tags
  updateTwitterTag("twitter:title", metaData.title);
  updateTwitterTag("twitter:description", metaData.description);
};

/**
 * Updates a standard meta tag
 * @param {string} name - Meta tag name attribute
 * @param {string} content - Meta tag content
 */
export const updateMetaTag = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) {
    tag.setAttribute("content", content);
  } else {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    tag.setAttribute("content", content);
    document.head.appendChild(tag);
  }
};

/**
 * Updates an Open Graph meta tag
 * @param {string} property - Meta tag property attribute
 * @param {string} content - Meta tag content
 */
export const updateOpenGraphTag = (property, content) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (tag) {
    tag.setAttribute("content", content);
  } else {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    tag.setAttribute("content", content);
    document.head.appendChild(tag);
  }
};

/**
 * Updates a Twitter Card meta tag
 * @param {string} name - Meta tag name attribute
 * @param {string} content - Meta tag content
 */
export const updateTwitterTag = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) {
    tag.setAttribute("content", content);
  } else {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    tag.setAttribute("content", content);
    document.head.appendChild(tag);
  }
};

/**
 * Updates the structured data script tag in the document head
 * @param {Object} structuredData - Structured data object to be serialized as JSON
 */
export const updateStructuredData = (structuredData) => {
  let scriptTag = document.querySelector('script[type="application/ld+json"]');
  if (scriptTag) {
    scriptTag.textContent = JSON.stringify(structuredData);
  } else {
    scriptTag = document.createElement("script");
    scriptTag.setAttribute("type", "application/ld+json");
    scriptTag.textContent = JSON.stringify(structuredData);
    document.head.appendChild(scriptTag);
  }
};

/**
 * Creates product structured data for schema.org markup
 * @param {Object} product - Product object
 * @param {Object} calculatedProduct - Calculated product with price adjustments
 * @param {Function} getImageUrl - Function to get proper image URL
 * @returns {Object} Structured data object
 */
export const createProductStructuredData = (product, calculatedProduct, getImageUrl) => {
  if (!product) return null;
  
  const productData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    description: product.shortDescription || product.longDescription,
    image:
      product.images && product.images.length > 0
        ? product.images.map((img) => getImageUrl(img))
        : [],
    sku: product.sku,
    mpn: product.id,
    brand: {
      "@type": "Brand",
      name: "KBros Leather",
    },
    offers: {
      "@type": "Offer",
      url: window.location.href,
      price: calculatedProduct
        ? calculatedProduct.calculatedPrice
        : product.price,
      priceCurrency: "USD",
      availability:
        calculatedProduct?.calculatedStock === 0 ||
        product.baseStockCount === 0
          ? "https://schema.org/OutOfStock"
          : "https://schema.org/InStock",
      seller: {
        "@type": "Organization",
        name: "KBros Leather",
      },
    },
  };
  
  if (product.reviews && product.reviews.length > 0) {
    productData.review = product.reviews.map((review) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating,
        bestRating: "5",
      },
      author: {
        "@type": "Person",
        name: review.author,
      },
      reviewBody: review.content,
    }));
  }
  
  return productData;
};
